import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { OutboundBuddyLogo } from '@/components/brand/OutboundBuddyLogo';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';

export default function Auth() {
  const navigate = useNavigate();
  const { signIn, signInWithGoogle, user, loading, refreshProfile } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Errors returned from OAuth redirect (e.g. before-user-created hook
  // rejection). Rendered prominently at the top of the auth card, separate
  // from the inline form-validation error.
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('reset') === 'true';
  });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [hasRecoverySession, setHasRecoverySession] = useState(false);
  const [recoverySessionChecked, setRecoverySessionChecked] = useState(false);
  const [resendingLink, setResendingLink] = useState(false);
  // Scanner-safe invite links (send-team-invitation, Oct 2026) carry the
  // recovery token HASH in the query string instead of Supabase's one-shot
  // verify URL. Nothing is redeemed on page load; the person presses
  // "Continue" and only then do we call verifyOtp. Mail-security link
  // scanners pre-open the page but never click, so the token survives.
  const [pendingTokenHash, setPendingTokenHash] = useState<string | null>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('reset') === 'true' ? urlParams.get('token_hash') : null;
  });
  const [verifyingLink, setVerifyingLink] = useState(false);

  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // "Remember me for 30 days" — controls whether Supabase persists the
  // session in localStorage (default ON, survives browser close) or
  // sessionStorage (session ends when last tab closes). Supabase client
  // reads the ob_remember_me flag via its custom storage wrapper (see
  // src/integrations/supabase/client.ts). We write the flag BEFORE
  // calling signIn so the very first setItem during auth routes to the
  // correct storage.
  const [rememberMe, setRememberMe] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    try {
      return window.localStorage.getItem('ob_remember_me') !== 'false';
    } catch {
      return true;
    }
  });

  // Listen for PASSWORD_RECOVERY event from Supabase
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        console.log('🔑 PASSWORD_RECOVERY event detected — showing set password form');
        setIsPasswordRecovery(true);
        setHasRecoverySession(true);
        setRecoverySessionChecked(true);
      }
    });

    // If reset URL param is present, check for valid session after Supabase processes the hash
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('reset') === 'true' && urlParams.get('token_hash')) {
      // Token-hash link: nothing to wait for. The session is created only
      // when the person clicks Continue (handleVerifyLink).
      console.log('🔑 Invite token hash detected — waiting for the user to continue');
      setRecoverySessionChecked(true);
    } else if (urlParams.get('reset') === 'true') {
      console.log('🔑 Reset URL param detected — waiting for session...');
      setTimeout(async () => {
        const { data: { session } } = await supabase.auth.getSession();
        setHasRecoverySession(!!session);
        setRecoverySessionChecked(true);
      }, 1500);
    }

    return () => subscription.unsubscribe();
  }, []);

  // Surface OAuth errors that Supabase appends to the redirect URL after a
  // failed sign-in (e.g. when the before-user-created hook rejects a
  // non-invited email). Errors arrive in either the query string or the URL
  // hash, depending on flow.
  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const errCode = search.get('error') || hash.get('error');
    const errDesc = search.get('error_description') || hash.get('error_description');

    if (!errCode && !errDesc) return;

    // Pretty-up the most common rejection from our before-user-created hook.
    const decoded = errDesc ? decodeURIComponent(errDesc.replace(/\+/g, ' ')) : null;
    const friendly = decoded?.includes("hasn't been invited")
      ? "This account hasn't been invited yet. Please ask an admin to invite you first."
      : decoded || errCode || 'Sign-in failed. Please try again.';

    setOauthError(friendly);

    // Strip the error params so they don't reappear on refresh.
    window.history.replaceState({}, '', '/auth');
  }, []);

  // Redirect if already authenticated (but NOT during password recovery).
  // Lands on the new UX dashboard (/dashboard); admins and client users
  // alike see the new design.
  useEffect(() => {
    if (!loading && user && !isPasswordRecovery) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate, isPasswordRecovery]);

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const { data: updated, error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        setError(error.message);
      } else {
        // The invite counts as accepted once the password is saved, not when
        // the link was opened (AuthContext skips stamping for link sessions).
        const userId = updated?.user?.id;
        if (userId) {
          const { error: stampError } = await supabase
            .from('profiles')
            .update({ invitation_accepted_at: new Date().toISOString() } as any)
            .eq('id', userId)
            .is('invitation_accepted_at', null);
          if (stampError) console.error('⚠️ Could not stamp invitation_accepted_at:', stampError);
          // ProtectedRoute sends link sessions with no accepted stamp back here,
          // so the cached profile must carry the stamp before we navigate away.
          await refreshProfile();
        }
        toast({
          title: "Password set successfully",
          description: "You are now logged in. Redirecting to your dashboard...",
          duration: 3000,
        });
        setIsPasswordRecovery(false);
        // Clear the URL params
        window.history.replaceState({}, '', '/auth');
        navigate('/dashboard');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        setError(error.message || 'Failed to start Google sign-in.');
        setGoogleLoading(false);
      }
      // On success the browser is redirected away to Google, so no further
      // state updates are needed here.
    } catch (err: any) {
      setError(err?.message || 'An unexpected error occurred. Please try again.');
      setGoogleLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Write the "Remember me" preference BEFORE signIn so Supabase's
    // custom storage (see src/integrations/supabase/client.ts) routes
    // the session tokens into the correct store on the first setItem.
    try {
      window.localStorage.setItem('ob_remember_me', rememberMe ? 'true' : 'false');
    } catch {
      // private mode / quota full — harmless; session falls back to
      // localStorage default.
    }

    try {
      const { error } = await signIn(email, password);
      
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please check your credentials and try again.');
        } else if (error.message.includes('Email not confirmed')) {
          setError('Please check your email and click the confirmation link before signing in.');
        } else {
          setError(error.message);
        }
      } else {
        // Navigation will happen automatically via useEffect
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Use localhost for development, production URL for production
      const redirectUrl = window.location.origin.includes('localhost') 
        ? 'http://localhost:8080/auth?reset=true'
        : `${window.location.origin}/auth?reset=true`;
        
      console.log('🔐 Sending password reset to:', resetEmail, 'Redirect URL:', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: redirectUrl,
      });
      
      if (error) {
        console.error('❌ Password reset error:', error);
        if (error.message.includes('rate limit')) {
          setError('Too many reset attempts. Please wait a few minutes and try again.');
        } else {
          setError(error.message);
        }
      } else {
        console.log('✅ Password reset email sent successfully');
        toast({
          title: "Password reset email sent",
          description: "Check your email for a link to reset your password.",
          duration: 6000,
        });
        setShowForgotPassword(false);
        setResetEmail('');
      }
    } catch (err) {
      console.error('❌ Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <OutboundBuddyLogo size="lg" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redeem a token-hash invite link. Runs only on the person's click, never
  // on page load, so a mail-security scanner that pre-opened the page has
  // not consumed the token. A real failure (already used, past expiry)
  // falls through to the Link Expired screen below.
  const handleVerifyLink = async () => {
    if (!pendingTokenHash) return;
    setVerifyingLink(true);
    setError(null);
    try {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: pendingTokenHash,
        type: 'recovery',
      });
      // The hash is single-use either way: drop it from the URL so a refresh
      // does not retry it.
      window.history.replaceState({}, '', '/auth?reset=true');
      setPendingTokenHash(null);
      if (error) {
        console.error('❌ Invite link could not be verified:', error);
        setHasRecoverySession(false);
      } else {
        setHasRecoverySession(true);
      }
      setRecoverySessionChecked(true);
    } catch (err: any) {
      console.error('❌ Unexpected error verifying invite link:', err);
      setPendingTokenHash(null);
      setHasRecoverySession(false);
      setRecoverySessionChecked(true);
    } finally {
      setVerifyingLink(false);
    }
  };

  // Resend invitation link for expired tokens
  const handleResendSetupLink = async () => {
    setResendingLink(true);
    setError(null);
    try {
      // Extract email from URL hash or ask user
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const emailFromUrl = hashParams.get('email');

      if (!emailFromUrl) {
        // Show forgot password flow as fallback
        setIsPasswordRecovery(false);
        setShowForgotPassword(true);
        setResendingLink(false);
        return;
      }

      const redirectUrl = `${window.location.origin}/auth?reset=true`;
      const { error } = await supabase.auth.resetPasswordForEmail(emailFromUrl, {
        redirectTo: redirectUrl,
      });
      if (error) throw error;
      toast({
        title: "New link sent!",
        description: "Check your email for a fresh password setup link.",
        duration: 6000,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to resend link');
    } finally {
      setResendingLink(false);
    }
  };

  // Show Set Password form during password recovery
  if (isPasswordRecovery) {
    // Still checking if we have a valid session
    if (!recoverySessionChecked) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-2">
                <img src="/outboundbuddy-logo.png" alt="Outbound Buddy" className="w-28 h-28 object-contain" />
              </div>
              <CardTitle className="text-2xl font-bold">Verifying your link...</CardTitle>
              <CardDescription>Please wait while we verify your invitation link.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      );
    }

    // Token-hash link: ask for a click before redeeming anything.
    if (pendingTokenHash && !hasRecoverySession) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-2">
                <img src="/outboundbuddy-logo.png" alt="Outbound Buddy" className="w-28 h-28 object-contain" />
              </div>
              <CardTitle className="text-2xl font-bold">Set up your account</CardTitle>
              <CardDescription>
                Click below to continue to your Outbound Buddy dashboard and choose a password.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button className="w-full" disabled={verifyingLink} onClick={handleVerifyLink}>
                {verifyingLink ? 'Verifying...' : 'Continue'}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Link expired — no valid session
    if (!hasRecoverySession) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-2">
                <img src="/outboundbuddy-logo.png" alt="Outbound Buddy" className="w-28 h-28 object-contain" />
              </div>
              <CardTitle className="text-2xl font-bold">Link Expired</CardTitle>
              <CardDescription>
                Your invitation link has expired. Please request a new one from your admin, or enter your email below to receive a fresh link.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="expired-email">Email</Label>
                <Input
                  id="expired-email"
                  type="email"
                  placeholder="Enter your email address"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                className="w-full"
                disabled={resendingLink || !resetEmail.trim()}
                onClick={async () => {
                  setResendingLink(true);
                  setError(null);
                  try {
                    const redirectUrl = `${window.location.origin}/auth?reset=true`;
                    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
                      redirectTo: redirectUrl,
                    });
                    if (error) throw error;
                    toast({
                      title: "New link sent!",
                      description: "Check your email for a fresh password setup link.",
                      duration: 6000,
                    });
                  } catch (err: any) {
                    setError(err.message || 'Failed to send link');
                  } finally {
                    setResendingLink(false);
                  }
                }}
              >
                {resendingLink ? 'Sending...' : 'Send New Setup Link'}
              </Button>

              <div className="text-center">
                <Button
                  variant="link"
                  className="text-sm text-muted-foreground p-0 h-auto"
                  onClick={() => {
                    setIsPasswordRecovery(false);
                    window.history.replaceState({}, '', '/auth');
                  }}
                >
                  Back to Sign In
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Valid session — show Set Password form
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              <img
                src="/outboundbuddy-logo.png"
                alt="Outbound Buddy"
                className="w-28 h-28 object-contain"
              />
            </div>
            <CardTitle className="text-2xl font-bold">Set Your Password</CardTitle>
            <CardDescription>
              Create a password for your Outbound Buddy account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSetNewPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password (min 8 characters)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Setting password...' : 'Set Password & Continue'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <img
              src="/outboundbuddy-logo.png"
              alt="Outbound Buddy"
              className="w-28 h-28 object-contain"
            />
          </div>

          <CardTitle className="text-2xl font-bold">Welcome Back</CardTitle>
          <CardDescription>
            Sign in to your Outbound Buddy account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {oauthError && (
            <Alert
              variant="destructive"
              className="mb-4 border-2 border-red-500 bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-100 dark:border-red-500"
            >
              <AlertCircle className="h-4 w-4 !text-red-600 dark:!text-red-400" />
              <AlertTitle className="font-semibold text-red-900 dark:text-red-100">
                Sign-in blocked
              </AlertTitle>
              <AlertDescription className="text-red-900/90 dark:text-red-100/90">
                {oauthError}
              </AlertDescription>
            </Alert>
          )}

          <Button
            type="button"
            variant="outline"
            className="w-full mb-4"
            onClick={handleGoogleSignIn}
            disabled={googleLoading || isLoading}
          >
            {/* Google "G" logo */}
            <svg
              className="w-4 h-4 mr-2"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {googleLoading ? 'Redirecting to Google…' : 'Sign in with Google'}
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">
                Or continue with email
              </span>
            </div>
          </div>

          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="signin-email">Email</Label>
              <Input
                id="signin-email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signin-password">Password</Label>
              <div className="relative">
                <Input
                  id="signin-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/*
              Remember me — default ON. When checked, the Supabase session
              persists in localStorage (survives browser close, valid for
              the 30-day refresh-token TTL). When unchecked, the session
              routes to sessionStorage and ends when the last tab closes.
              See rememberMeStorage in src/integrations/supabase/client.ts.
            */}
            <div className="flex items-center gap-2 py-1">
              <Checkbox
                id="remember-me"
                checked={rememberMe}
                onCheckedChange={(v) => setRememberMe(v === true)}
              />
              <Label
                htmlFor="remember-me"
                className="text-sm font-normal text-muted-foreground cursor-pointer"
              >
                Keep me signed in for 30 days
              </Label>
            </div>

            <div className="flex items-center justify-between">
              <Button
                type="submit"
                className="flex-1"
                disabled={isLoading}
              >
                <LogIn className="w-4 h-4 mr-2" />
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </div>

            <div className="text-center">
              <Button
                type="button"
                variant="link"
                className="text-sm text-muted-foreground p-0 h-auto"
                onClick={() => setShowForgotPassword(true)}
              >
                Forgot your password?
              </Button>
            </div>
          </form>
        </CardContent>
       </Card>

       {/* Forgot Password Dialog */}
       {showForgotPassword && (
         <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
           <Card className="w-full max-w-md">
             <CardHeader>
               <CardTitle>Reset Password</CardTitle>
               <CardDescription>
                 Enter your email address and we'll send you a link to reset your password.
               </CardDescription>
             </CardHeader>
             <CardContent>
               <form onSubmit={handleForgotPassword} className="space-y-4">
                 <div className="space-y-2">
                   <Label htmlFor="reset-email">Email</Label>
                   <Input
                     id="reset-email"
                     type="email"
                     placeholder="Enter your email"
                     value={resetEmail}
                     onChange={(e) => setResetEmail(e.target.value)}
                     required
                   />
                 </div>
                 
                 {error && (
                   <Alert variant="destructive">
                     <AlertDescription>{error}</AlertDescription>
                   </Alert>
                 )}
                 
                 <div className="flex gap-2">
                   <Button 
                     type="submit" 
                     className="flex-1" 
                     disabled={isLoading}
                   >
                     {isLoading ? 'Sending...' : 'Send Reset Link'}
                   </Button>
                   <Button
                     type="button"
                     variant="outline"
                     onClick={() => {
                       setShowForgotPassword(false);
                       setResetEmail('');
                       setError(null);
                     }}
                   >
                     Cancel
                   </Button>
                 </div>
               </form>
             </CardContent>
           </Card>
         </div>
       )}
     </div>
   );
}